import React, { useState, useEffect } from 'react';
import { dayjs } from '@umanforge/common';
import ToggleButton from '@common_components/buttons/toggleButton.component';

interface WeekDaysPropsSelect {
  defaultSelectedDays?: DaysToShow;
  shownDays?: DaysToShow;
  label?: string;
  className?: string;
  labelPosition?: 'top' | 'left';
  textSize?: string;
  onSelectionChange: ( selectedIndices: DaysToShow ) => void;
  canBeEmpty?: boolean;
  disable?: boolean;
}

export interface DaysToShow {
  sunday: boolean;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
}

const WeekDaysSelect: React.FC<WeekDaysPropsSelect> = ( {
  defaultSelectedDays = { sunday: false, monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false },
  shownDays = { sunday: false, monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false },
  label = '',
  className = '',
  labelPosition = 'left',
  textSize = 'w-3/12 mr-4',
  onSelectionChange,
  canBeEmpty = false,
  disable = false,
} ) => {

  const [ iLocale, ] = useState<ILocale>( dayjs.Ls[ dayjs.locale() ] );
  const [ weekStart, ] = useState<number>( iLocale.weekStart ?? 0 );
  const [ dayNames, ] = useState<string[]>( iLocale.weekdays ?? [] );

  const [ selectedDays, setSelectedDays ] = useState<number[]>( [ 0, 0, 0, 0, 0, 0, 0 ] );

  useEffect( () => {
    setSelectedDays( [
      defaultSelectedDays.sunday ? 1 : 0,
      defaultSelectedDays.monday ? 1 : 0,
      defaultSelectedDays.tuesday ? 1 : 0,
      defaultSelectedDays.wednesday ? 1 : 0,
      defaultSelectedDays.thursday ? 1 : 0,
      defaultSelectedDays.friday ? 1 : 0,
      defaultSelectedDays.saturday ? 1 : 0,
    ] );
  }, [ , defaultSelectedDays ] );

  const handleButtonClick = ( index: number ) => {
    if ( disable ) { return; }

    const selectedDaysCop = [ ...selectedDays ];
    selectedDaysCop[ index ] = selectedDays[ index ] ? 0 : 1;
    if ( !canBeEmpty && selectedDaysCop.reduce( ( a, b ) => { return a + b; } ) == 0 ) {
      return; // do not handle button click if the result is empty
    }
    setSelectedDays( selectedDaysCop );

    onSelectionChange( {
      sunday: selectedDaysCop[ 0 ] == 1,
      monday: selectedDaysCop[ 1 ] == 1,
      tuesday: selectedDaysCop[ 2 ] == 1,
      wednesday: selectedDaysCop[ 3 ] == 1,
      thursday: selectedDaysCop[ 4 ] == 1,
      friday: selectedDaysCop[ 5 ] == 1,
      saturday: selectedDaysCop[ 6 ] == 1
    } );
  };

  const shouldBeShown = ( index: number ): boolean => {
    switch ( index ) {
    case 0: return shownDays.sunday;
    case 1: return shownDays.monday;
    case 2: return shownDays.tuesday;
    case 3: return shownDays.wednesday;
    case 4: return shownDays.thursday;
    case 5: return shownDays.friday;
    case 6: return shownDays.saturday;
    default: return false;
    }
  };

  return (
    <div className={`w-full ${className} ${labelPosition === 'left' ? 'flex-center' : ''}`}>
      {label && (
        <label className={`text-gray-600 ${textSize} ${labelPosition === 'left' ? 'flex items-center' : ''}`}>
          {label}
        </label>
      )}
      <div className="flex flex-wrap gap-2 w-full" key={'' + shownDays.monday + shownDays.tuesday + shownDays.wednesday + shownDays.thursday + shownDays.friday + shownDays.saturday + shownDays.sunday }>
        {dayNames.map( ( _, index ) => {
          index = ( index + weekStart ) % 7;
          if ( !shouldBeShown( index ) ) {
            return <div key={index}/>;
          }
          return (
            <ToggleButton
              key={index}
              label={dayNames[ index ]}
              isSelected={selectedDays[ index ] == 1}
              onClick={() => {return handleButtonClick( index );}}
              disable={disable}
            />
          );
        } )}
      </div>
    </div>
  );
};

export default WeekDaysSelect;
